<template>
  <div class="admin-panel">
    <button class="back-btn" @click="$emit('back')">← Назад к событиям</button>
    <h1>Админ-панель</h1>
    <h2>{{ eventName }}</h2>

    <!-- Блок загрузки фона для этого события -->
    <div class="background-upload-section">
      <h3>Фон для экрана результатов</h3>
      <p class="hint">
        Загрузите изображение (JPG/PNG, до 5 МБ) — оно будет отображаться на экране результатов именно для этого события.
      </p>

      <input
        type="file"
        name="file"
        ref="bgUploadInput"
        accept="image/jpeg, image/png, image/jpg"
        style="display: none;"
        @change="handleBackgroundUpload"
      />

      <button
        @click="triggerBackgroundUpload"
        :disabled="isUploadingBackground"
        class="upload-bg-btn"
      >
        {{ isUploadingBackground ? 'Загружаем...' : 'Загрузить / заменить фон' }}
      </button>

      <p v-if="uploadMessage" :class="{ 'upload-success': uploadSuccess, 'upload-error': !uploadSuccess }">
        {{ uploadMessage }}
      </p>
    </div>

    <div class="grid">
      <!-- Левая колонка — номинации -->
      <div class="left-panel">
        <h3>Номинации</h3>
        <div
          v-for="(participants, category) in categories"
          :key="category"
          class="category-item"
        >
          <div class="category-name">{{ category }}</div>
          <button
            @click="publishCategory(category)"
            :disabled="published[category] || isPublishing"
            class="publish-btn"
            :class="{ published: published[category] }"
          >
            <span v-if="isPublishing && publishingCategory === category">
              Отправка...
            </span>
            <span v-else-if="published[category]">Опубликовано</span>
            <span v-else>Опубликовать</span>
          </button>
        </div>
        <button @click="resetPublished" class="reset-btn">
          Сбросить статус публикаций (для теста)
        </button>
      </div>

      <!-- Правая колонка — живое отображение -->
      <div class="right-panel">
        <h3>Активная номинация (на экране сейчас)</h3>
        <div class="connection-status" :class="{ connected: wsConnected }">
          {{ wsConnected ? "Подключено" : "Соединение..." }}
        </div>
        <pre class="live-json">{{ liveData || "Ждём первую публикацию..." }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AdminPanel",
  props: {
    eventName: { type: String, required: true },
    eventData: { type: Object, required: true },
  },
  data: function () {
    return {
      ws: null,
      wsConnected: false,
      liveData: null,
      published: JSON.parse(
        localStorage.getItem("published_" + this.eventName) || "{}"
      ),
      isPublishing: false,
      isActive: true,
      publishingCategory: null,

      // Поля для загрузки фона
      isUploadingBackground: false,
      uploadMessage: '',
      uploadSuccess: false,
    };
  },
  computed: {
    categories: function () {
      return this.eventData.participants || {};
    },
  },
  mounted: function () {
    console.log("[AdminPanel] Монтируется для события: " + this.eventName);
    this.connectWebSocket();
    this.loadCurrentJson();
  },
  beforeDestroy: function () {
    console.log("[AdminPanel] Компонент уничтожается");
    this.isActive = false;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
    }
  },
  methods: {
    connectWebSocket: function () {
      var protocol = location.protocol === "https:" ? "wss:" : "ws:";
      var wsUrl = protocol + "//" + location.host + "/ws-dance";

      console.log("[AdminPanel] Пытаемся подключиться к WebSocket: " + wsUrl);
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = function () {
        this.wsConnected = true;
        console.log("[AdminPanel] WebSocket подключён");
        this.ws.send(JSON.stringify({
          action: "subscribe",
          event: this.eventName,
        }));
      }.bind(this);

      this.ws.onmessage = function () {
        console.log("[AdminPanel] Получено сообщение от сервера – обновляем данные");
        this.loadCurrentJson();
      }.bind(this);

      this.ws.onclose = function (event) {
        this.wsConnected = false;
        console.log("[AdminPanel] WebSocket закрыт (code: " + event.code + ")");

        if (this.isActive) {
          console.log("[AdminPanel] Переподключаемся через 3 секунды...");
          setTimeout(function () { this.connectWebSocket(); }.bind(this), 3000);
        } else {
          console.log("[AdminPanel] Компонент уничтожен — переподключение отменено");
        }
      }.bind(this);

      this.ws.onerror = function (err) {
        console.warn("[AdminPanel] Ошибка WebSocket", err);
        this.wsConnected = false;
      }.bind(this);
    },

    loadCurrentJson: function () {
      var self = this;
      try {
        var safeName = this.eventName.replace(/[^a-zA-Z0-9а-яА-ЯёЁ\s._-]/g, "");
        var url = "https://letsdancescores.tech/dance_data/active/" + safeName + ".json?t=" + Date.now();
        fetch(url)
          .then(function (res) {
            if (res.ok) {
              return res.json();
            } else {
              throw new Error("Не ок");
            }
          })
          .then(function (data) {
            self.liveData = JSON.stringify(data, null, 2);
            console.log("[AdminPanel] Данные активной номинации загружены");
          })
          .catch(function (e) {
            console.error("[AdminPanel] Ошибка загрузки активных данных", e);
            self.liveData = "Ошибка загрузки";
          });
      } catch (e) {
        console.error("[AdminPanel] Ошибка загрузки активных данных", e);
        self.liveData = "Ошибка загрузки";
      }
    },

    publishCategory: function (category) {
      this.isPublishing = true;
      this.publishingCategory = category;
      console.log("[AdminPanel] Публикация категории: " + category);

      var self = this;
      fetch("https://letsdancescores.tech/danceapi/publish-results.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: this.eventName,
          category: category,
          participants: this.categories[category],
        }),
      })
        .then(function (res) { return res.json(); })
        .then(function (result) {
          if (result.success) {
            self.published[category] = true;
            self.savePublishedState();
            console.log("[AdminPanel] Категория \"" + category + "\" успешно опубликована");
            setTimeout(function () { self.loadCurrentJson(); }, 1200);
          } else {
            alert("Ошибка публикации: " + (result.error || "неизвестно"));
            console.error("[AdminPanel] Ошибка публикации", result);
          }
        })
        .catch(function (e) {
          alert("Нет связи с сервером");
          console.error("[AdminPanel] Ошибка при публикации", e);
        })
        .finally(function () {
          self.isPublishing = false;
          self.publishingCategory = null;
        });
    },

    savePublishedState: function () {
      localStorage.setItem(
        "published_" + this.eventName,
        JSON.stringify(this.published)
      );
    },

    resetPublished: function () {
      console.log("[AdminPanel] Сброс состояния публикаций (тест)");
      this.published = {};
      localStorage.removeItem("published_" + this.eventName);
      this.loadCurrentJson();
    },

    triggerBackgroundUpload: function () {
      this.$refs.bgUploadInput.click();
    },

    handleBackgroundUpload: function (event) {
      var files = event.target.files;
      var file = files && files[0];
      if (!file) {
        console.error("[DEBUG] Нет файла — event.target.files пустой");
        this.uploadMessage = 'Файл не выбран';
        return;
      }

      console.log('[DEBUG] Файл выбран:', file.name, file.size + ' байт', file.type);

      var formData = new FormData();
      formData.append('event', this.eventName);
      formData.append('file', file);

      // Отладка FormData
      var entries = [];
      formData.forEach(function (value, key) {
        if (value instanceof File) {
          entries.push(key + ": " + value.name + " (" + value.size + " байт, " + value.type + ")");
        } else {
          entries.push(key + ": " + value);
        }
      });
      console.log("[FormData] " + entries.join("\n"));

      this.isUploadingBackground = true;
      this.uploadMessage = 'Загружаем фон...';
      this.uploadSuccess = false;

      var self = this;
      fetch('https://letsdancescores.tech/danceapi/upload-background.php', {
        method: 'POST',
        body: formData,
      })
        .then(function (res) {
          console.log('[DEBUG] Ответ сервера статус:', res.status);
          return res.json();
        })
        .then(function (result) {
          console.log('[DEBUG] Полный ответ:', result);
          if (result.success) {
            self.uploadSuccess = true;
            self.uploadMessage = 'Фон успешно загружен и заменён!';
            console.log('[AdminPanel] Фон загружен:', result.url);
            setTimeout(function () { self.loadCurrentJson(); }, 1000);
          } else {
            self.uploadSuccess = false;
            self.uploadMessage = result.error || 'Ошибка загрузки фона';
            console.error('[AdminPanel] Ответ сервера (ошибка):', result);
          }
        })
        .catch(function (err) {
          console.error('[AdminPanel] Fetch-ошибка:', err);
          self.uploadMessage = 'Нет связи или ошибка сети';
        })
        .finally(function () {
          self.isUploadingBackground = false;
          event.target.value = '';
          setTimeout(function () { self.uploadMessage = ''; }, 6000);
        });
    },
  },
};
</script>

<style scoped>
.background-upload-section {
  margin: 16px 0 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.hint {
  color: #555;
  font-size: 0.9em;
  margin: 8px 0 16px;
}

.upload-bg-btn {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 1em;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.upload-bg-btn:hover:not(:disabled) {
  background-color: #45a049;
}

.upload-bg-btn:disabled {
  background: #aaa;
  cursor: not-allowed;
}

.upload-success {
  color: #2e7d32;
  font-weight: 500;
  margin-top: 12px;
}

.upload-error {
  color: #d32f2f;
  margin-top: 12px;
}
.admin-panel {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
}
.back-btn {
  padding: 12px 24px;
  background: #555;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 20px;
}
h1 {
  text-align: center;
  color: #d32f2f;
  margin: 10px 0;
}
h2 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}
.left-panel,
.right-panel {
  background: white;
  padding: 25px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}
.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  margin: 10px 0;
  border-radius: 12px;
  font-size: 16px;
}
.category-name {
  flex: 1;
  font-weight: 600;
}
.publish-btn {
  padding: 12px 28px;
  border: none;
  border-radius: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: 0.3s;
}
.publish-btn {
  background: #d32f2f;
  color: white;
}
.publish-btn:hover:not(:disabled) {
  background: #b71c1c;
}
.publish-btn.published {
  background: #43a047;
  cursor: default;
}
.reset-btn {
  margin-top: 30px;
  padding: 14px;
  background: #ff9800;
  color: white;
  border: none;
  border-radius: 10px;
  width: 100%;
  cursor: pointer;
}
.connection-status {
  padding: 10px 16px;
  border-radius: 8px;
  display: inline-block;
  margin-bottom: 15px;
  font-weight: bold;
}
.connection-status.connected {
  background: #e8f5e9;
  color: #2e7d32;
}
.connection-status:not(.connected) {
  background: #ffebee;
  color: #c62828;
}
.live-json {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 12px;
  max-height: 70vh;
  overflow: auto;
  font-family: "Courier New", monospace;
  font-size: 15px;
  white-space: pre;
  border: 1px solid #ddd;
}
</style>